import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Users, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

type InviteInfo = { ownerName: string | null; email: string | null; valid: boolean; accepted: boolean };

export function JoinTeam() {
  const { token } = useParams<{ token: string }>();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  const query = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InviteInfo>(`/api/public/team/invite/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api('/api/team/accept', { method: 'POST', body: JSON.stringify({ token }) }),
    onSuccess: async () => {
      await refresh();
      setDone(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
    },
  });

  const joinPath = `/join/${token}`;

  return (
    <Shell>
      {query.isLoading ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 className="animate-spin" size={28} /></div>
      ) : query.isError || !query.data?.valid ? (
        <Centered
          icon={<XCircle size={32} className="text-red-500" />}
          title={query.data?.accepted ? 'Invitación ya utilizada' : 'Invitación no válida'}
          body={query.data?.accepted
            ? 'Esta invitación ya se usó. Pide una nueva a quien te invitó.'
            : 'Este enlace de invitación no existe o ha caducado.'}
        />
      ) : done ? (
        <Centered
          icon={<CheckCircle2 size={32} className="text-emerald-500" />}
          title="¡Te has unido al equipo!"
          body="Redirigiéndote al panel…"
        />
      ) : (
        <div className="text-center py-6">
          <div className="mx-auto w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center">
            <Users size={26} className="text-brand-600" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">
            Únete al equipo{query.data.ownerName ? ` de ${query.data.ownerName}` : ''}
          </h1>
          <p className="mt-2 text-slate-600">
            Te han invitado a colaborar en Zettapost. Accederás a su espacio según los permisos asignados.
          </p>

          {user ? (
            <>
              <button onClick={() => accept.mutate()} disabled={accept.isPending} className="btn-primary gap-2 mt-6 inline-flex">
                {accept.isPending ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />}
                Aceptar invitación
              </button>
              <p className="mt-2 text-xs text-slate-400">Conectado como {user.email}</p>
              {accept.isError && (
                <p className="mt-3 text-sm text-red-600">
                  {accept.error instanceof ApiError && accept.error.code === 'ALREADY_MEMBER'
                    ? 'Ya formas parte de un equipo.'
                    : accept.error instanceof ApiError && accept.error.code === 'SELF'
                      ? 'No puedes unirte a tu propio equipo.'
                      : accept.error instanceof ApiError && accept.error.code === 'LIMIT_REACHED'
                        ? 'El equipo está completo.'
                        : 'No se pudo aceptar la invitación.'}
                </p>
              )}
            </>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row gap-2.5 justify-center">
              <Link to={`/register?next=${encodeURIComponent(joinPath)}`} className="btn-primary">Crear cuenta y unirme</Link>
              <Link to={`/login?next=${encodeURIComponent(joinPath)}`} className="btn-secondary">Ya tengo cuenta</Link>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

function Centered({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="text-center py-10">
      <div className="mx-auto w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center">{icon}</div>
      <h1 className="mt-4 text-xl font-bold text-slate-900">{title}</h1>
      <p className="mt-2 text-slate-600 max-w-sm mx-auto">{body}</p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center">
          <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-8 w-auto" />
        </div>
      </header>
      <main className="max-w-lg mx-auto px-4 py-10">
        <div className="card">{children}</div>
      </main>
    </div>
  );
}
